// Trend ("динамика") of one series: what changed between the two latest
// readings, and — for reports that are mostly qualitative, such as a
// spermogram's Liquefaction/Viscosity/Colour or its +/++ motility grades —
// how a non-numeric value moved over time.
//
// Everything here is arithmetic and comparison over values and reference
// bounds *printed on the documents themselves* (docs/AI_RULES.md): direction
// of change, size of change, and whether the reading moved toward or away
// from the range that was printed. Nothing here decides whether a change is
// good or bad — "higher is better" differs per analyte and is exactly the
// kind of clinical judgement this app does not make.

import { inRange } from './labMath';
import type { LabResult } from './localData';
import { formatLabNumber } from './labReference';

export type TrendDirection = 'up' | 'down' | 'flat';

// How the latest reading sits against its printed range compared to the
// previous one. Null when either reading has no printed range to judge
// against — deliberately distinct from "nothing changed".
export type RangeShift = 'entered' | 'left' | 'closer' | 'further' | 'stable-in' | 'stable-out';

export interface TrendPoint { takenOn: string; value: number }

export interface NumericTrend {
  direction: TrendDirection;
  delta: number;
  // Undefined when the previous value is 0 — a percentage change from zero is
  // not a number anyone can act on.
  deltaPercent?: number;
  // Below this, wording says "почти не изменился" instead of naming a
  // direction: a 0.3% difference between two lab draws is noise, not a trend.
  significant: boolean;
  previous: TrendPoint;
  latest: TrendPoint;
  first: TrendPoint;
  readingCount: number;
  rangeShift: RangeShift | null;
}

const SIGNIFICANT_PERCENT = 5;

// dd.MM.yyyy without Intl: the same string has to come out of a test run, a
// phone, and a desktop browser regardless of the platform's locale data.
export function formatIsoDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!match) return iso || '';
  return `${match[3]}.${match[2]}.${match[1]}`;
}

// 0 when the value is inside the printed range (or there is nothing to judge
// against); otherwise how far outside it sits, in the reading's own unit.
function distanceOutsideRange(reading: LabResult & { value: number }): number {
  if (reading.refLow !== undefined && reading.value < reading.refLow) return reading.refLow - reading.value;
  if (reading.refHigh !== undefined && reading.value > reading.refHigh) return reading.value - reading.refHigh;
  return 0;
}

function samePrintedRange(left: LabResult, right: LabResult): boolean {
  return left.refLow === right.refLow && left.refHigh === right.refHigh;
}

function rangeShiftBetween(previous: LabResult & { value: number }, latest: LabResult & { value: number }): RangeShift | null {
  const previousStatus = inRange(previous.value, previous.refLow, previous.refHigh);
  const latestStatus = inRange(latest.value, latest.refLow, latest.refHigh);
  if (previousStatus === null || latestStatus === null) return null;
  if (!previousStatus && latestStatus) return 'entered';
  if (previousStatus && !latestStatus) return 'left';
  if (previousStatus && latestStatus) return 'stable-in';
  // Both outside. "Closer"/"further" is only meaningful while the printed
  // bounds stay the same — a different lab printing a different range makes
  // the two distances incomparable, and "still outside" is then the only
  // claim the documents support.
  if (!samePrintedRange(previous, latest)) return 'stable-out';
  const previousDistance = distanceOutsideRange(previous);
  const latestDistance = distanceOutsideRange(latest);
  if (latestDistance < previousDistance) return 'closer';
  if (latestDistance > previousDistance) return 'further';
  return 'stable-out';
}

// Null for a series with fewer than two numeric readings — there is no
// direction to report from a single point. Readings must already be sorted by
// takenOn (seriesReadingsForKey does that).
export function numericTrend(readings: LabResult[]): NumericTrend | null {
  const numeric = readings.filter((reading): reading is LabResult & { value: number } => reading.value !== undefined);
  if (numeric.length < 2) return null;
  const latest = numeric[numeric.length - 1];
  const previous = numeric[numeric.length - 2];
  const first = numeric[0];
  const delta = latest.value - previous.value;
  const deltaPercent = previous.value === 0 ? undefined : (delta / Math.abs(previous.value)) * 100;

  return {
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
    delta,
    deltaPercent,
    significant: deltaPercent === undefined ? delta !== 0 : Math.abs(deltaPercent) >= SIGNIFICANT_PERCENT,
    previous: { takenOn: previous.takenOn, value: previous.value },
    latest: { takenOn: latest.takenOn, value: latest.value },
    first: { takenOn: first.takenOn, value: first.value },
    readingCount: numeric.length,
    rangeShift: rangeShiftBetween(previous, latest),
  };
}

// Compact badge text for a metric card: "↓ −28%" / "↑ +3.6" / "= без изменений".
export function formatTrendDelta(trend: NumericTrend): string {
  if (trend.delta === 0) return '= без изменений';
  const arrow = trend.direction === 'up' ? '↑' : '↓';
  const sign = trend.delta > 0 ? '+' : '−';
  const magnitude = trend.deltaPercent !== undefined
    ? `${sign}${formatLabNumber(Math.abs(Math.round(trend.deltaPercent * 10) / 10))}%`
    : `${sign}${formatLabNumber(Math.abs(trend.delta))}`;
  return `${arrow} ${magnitude}`;
}

const RANGE_SHIFT_TEXT: Record<RangeShift, string> = {
  entered: 'Последний замер попал в напечатанную на бланке норму.',
  left: 'Последний замер вышел за напечатанную на бланке норму.',
  closer: 'Замер по-прежнему вне нормы, но ближе к ней, чем предыдущий.',
  further: 'Замер по-прежнему вне нормы и дальше от неё, чем предыдущий.',
  'stable-in': 'Оба замера в пределах напечатанной на бланке нормы.',
  'stable-out': 'Оба замера вне напечатанной на бланке нормы.',
};

// Neutral, factual description of the change — no interpretation of whether
// the direction is good, and no cause. Unit is appended only when the report
// printed one.
export function trendSentence(trend: NumericTrend, unit?: string): string {
  const suffix = unit ? ` ${unit}` : '';
  const move = `${formatIsoDate(trend.previous.takenOn)} → ${formatIsoDate(trend.latest.takenOn)}: `
    + `${formatLabNumber(trend.previous.value)} → ${formatLabNumber(trend.latest.value)}${suffix}`;

  const change = trend.delta === 0
    ? 'значение не изменилось'
    : !trend.significant
      ? `изменение незначительное (${formatTrendDelta(trend)})`
      : `${trend.direction === 'up' ? 'рост' : 'снижение'} на ${formatLabNumber(Math.abs(trend.delta))}${suffix}`
        + (trend.deltaPercent === undefined ? '' : ` (${formatLabNumber(Math.abs(Math.round(trend.deltaPercent * 10) / 10))}%)`);

  const shift = trend.rangeShift
    ? ` ${RANGE_SHIFT_TEXT[trend.rangeShift]}`
    : ' Норма на бланке не указана — сравниваются только сами замеры.';

  return `${move} — ${change}.${shift}`;
}

// ---------------------------------------------------------------------------
// Qualitative series
// ---------------------------------------------------------------------------

// Some indicators never carry a number. A spermogram prints Liquefaction /
// Viscosity / Colour as "Normal" and grades motility as "+" … "++++", so a
// chart of numbers would show nothing at all for most of the report.
//
// The ladders below order *printed symbols*, not medical severity: "++" is
// more plus signs than "+", "Positive" means the thing was found and
// "Negative" that it was not. That ordering is what lets the timeline be
// drawn as steps; it never says which end is healthier. A series whose values
// don't all fit one ladder is still shown as a dated timeline of the exact
// printed texts, just without a vertical axis.
interface OrdinalScale {
  id: 'plus' | 'presence' | 'normality';
  // Index = level, low to high. Each entry lists the spellings seen for it.
  levels: { label: string; spellings: string[] }[];
}

const ORDINAL_SCALES: OrdinalScale[] = [
  {
    id: 'plus',
    levels: [
      { label: '−', spellings: ['-', '−', '–'] },
      { label: '+', spellings: ['+'] },
      { label: '++', spellings: ['++'] },
      { label: '+++', spellings: ['+++'] },
      { label: '++++', spellings: ['++++'] },
    ],
  },
  {
    id: 'presence',
    levels: [
      { label: 'Отрицательно', spellings: ['negative', 'neg', 'отрицательно', 'отрицательный', 'не обнаружено', 'не обнаружен'] },
      { label: 'Положительно', spellings: ['positive', 'pos', 'положительно', 'положительный', 'обнаружено', 'обнаружен'] },
    ],
  },
  {
    id: 'normality',
    levels: [
      { label: 'Не норма', spellings: ['abnormal', 'не норма', 'не в норме', 'патология', 'патологический'] },
      { label: 'Норма', spellings: ['normal', 'norm', 'норма', 'в норме', 'нормальный'] },
    ],
  },
];

function normalizeText(value: string): string {
  return value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru-RU');
}

function levelIn(scale: OrdinalScale, value: string): number | null {
  const normalized = normalizeText(value);
  const index = scale.levels.findIndex(level => level.spellings.includes(normalized));
  return index === -1 ? null : index;
}

export interface QualitativePoint {
  id: string;
  takenOn: string;
  text: string;
  // Present only when every reading of the series fits the same ladder.
  level?: number;
}

export interface QualitativeTrend {
  points: QualitativePoint[];
  // Absent for a series that doesn't fit any ladder — the timeline is then
  // just dated labels.
  scale?: { id: OrdinalScale['id']; labels: string[] };
  changed: boolean;
  lastChange?: { from: string; to: string; takenOn: string };
}

// Null when the series has no text readings at all. Readings must already be
// sorted by takenOn.
export function qualitativeTrend(readings: LabResult[]): QualitativeTrend | null {
  const textReadings = readings.filter(reading => reading.value === undefined && Boolean(reading.valueText?.trim()));
  if (!textReadings.length) return null;

  const scale = ORDINAL_SCALES.find(candidate => textReadings.every(reading => levelIn(candidate, reading.valueText || '') !== null));
  const points: QualitativePoint[] = textReadings.map(reading => ({
    id: reading.id,
    takenOn: reading.takenOn,
    text: (reading.valueText || '').trim(),
    level: scale ? levelIn(scale, reading.valueText || '') ?? undefined : undefined,
  }));

  let lastChange: QualitativeTrend['lastChange'];
  for (let index = 1; index < points.length; index += 1) {
    if (normalizeText(points[index].text) !== normalizeText(points[index - 1].text)) {
      lastChange = { from: points[index - 1].text, to: points[index].text, takenOn: points[index].takenOn };
    }
  }

  return {
    points,
    scale: scale ? { id: scale.id, labels: scale.levels.map(level => level.label) } : undefined,
    changed: Boolean(lastChange),
    lastChange,
  };
}

export function qualitativeSentence(trend: QualitativeTrend): string {
  const latest = trend.points[trend.points.length - 1];
  if (trend.points.length < 2) {
    return `Один замер: «${latest.text}» от ${formatIsoDate(latest.takenOn)}. Динамика появится со второго замера.`;
  }
  if (!trend.lastChange) {
    return `Значение не менялось: «${latest.text}» во всех замерах (${trend.points.length}), последний — ${formatIsoDate(latest.takenOn)}.`;
  }
  return `Последнее изменение ${formatIsoDate(trend.lastChange.takenOn)}: «${trend.lastChange.from}» → «${trend.lastChange.to}».`;
}
